<template>
  <header
    class="site-header sticky top-0 z-[1002] bg-white shadow-[0_1px_0_#e9ecef] transition-shadow duration-[220ms]"
    :class="{ 'is-scrolled shadow-[0_4px_20px_rgba(0,0,0,0.10)]': isScrolled }"
  >

    <!-- ① Smart topbar: roteert tussen urgentie / aanbieding / vertrouwen -->
    <div v-if="!isScrolled" class="hidden md:block bg-[#111] text-white text-[0.8rem] py-[0.45rem]">
      <div class="max-w-screen-xl mx-auto px-4 flex items-center justify-between gap-4">

        <!-- Roterende berichten (links/midden) -->
        <div class="topbar-messages relative flex-1 h-[1.25rem] overflow-hidden">
          <transition name="topbar-slide">
            <span
              :key="activeTopbarMsg"
              class="absolute inset-0 flex items-center gap-2 whitespace-nowrap font-medium"
            >
              <!-- Bericht 0: urgentie (countdown) -->
              <template v-if="activeTopbarMsg === 0">
                <i class="bi bi-alarm text-[#FFD500] text-[0.875rem]"></i>
                <template v-if="!cutoffPassed && countdownTime">
                  Nog <strong class="text-[#FFD500] mx-1">{{ countdownTime }}</strong> om te bestellen,
                  <strong class="text-[#FFD500] ml-1">morgen in huis!</strong>
                </template>
                <template v-else>
                  Morgen vóór <strong class="text-[#FFD500] mx-1">15:00</strong> besteld?
                  <strong class="text-[#FFD500] ml-1">Overmorgen in huis!</strong>
                </template>
              </template>
              <!-- Bericht 1: aanbieding -->
              <template v-else-if="activeTopbarMsg === 1">
                <i class="bi bi-lightning-charge text-[#FFD500] text-[0.875rem]"></i>
                <span>Nu <strong class="text-[#FFD500]">10% korting</strong> op alle laadkabels + gratis draagtas bij je bestelling</span>
              </template>
              <!-- Bericht 2: vertrouwen -->
              <template v-else>
                <i class="bi bi-shield-check text-[#FFD500] text-[0.875rem]"></i>
                <span><strong class="text-[#FFD500]">Gratis verzending</strong> v.a. €50 &nbsp;·&nbsp; <strong class="text-[#FFD500]">2 jaar</strong> garantie &nbsp;·&nbsp; Veilig betalen</span>
              </template>
            </span>
          </transition>
        </div>

        <!-- Utility links (rechts) -->
        <div class="flex items-center gap-3 shrink-0 text-white/65">
          <router-link to="/klantenservice" class="text-white/65 no-underline hover:text-white transition-colors duration-150 text-[0.8rem]">Klantenservice</router-link>
          <span class="text-white/25">|</span>
          <router-link to="/veelgestelde-vragen" class="text-white/65 no-underline hover:text-white transition-colors duration-150 text-[0.8rem]">FAQ</router-link>
          <span class="text-white/25">|</span>
          <span class="flex items-center gap-1 cursor-pointer text-white/65 hover:text-white transition-colors duration-150 text-[0.8rem]">
            🇳🇱 NL
          </span>
        </div>

      </div>
    </div>

    <!-- ③ Main Header -->
    <div class="bg-white transition-[padding] duration-[220ms]" :class="isScrolled ? 'py-2' : 'py-4'">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="flex items-center gap-2 md:gap-6">

          <!-- Hamburger: links op mobiel -->
          <button type="button" :aria-label="showMobileMenu ? 'Menu sluiten' : 'Menu openen'" :aria-expanded="showMobileMenu" class="md:hidden bg-transparent border-none text-[1.375rem] text-[#333] cursor-pointer p-2 -ml-1" @click="toggleMobileMenu">
            <i class="bi" :class="showMobileMenu ? 'bi-x' : 'bi-list'" aria-hidden="true"></i>
          </button>

          <!-- Logo - CSS-component, geen font-afhankelijkheid -->
          <router-link to="/" class="shrink-0 flex items-center no-underline" aria-label="Plug2Charge.nl - home">
            <Logo :height="isScrolled ? 36 : 48" />
          </router-link>

          <!-- Search Bar: verborgen op mobiel -->
          <div class="search-bar hidden md:flex flex-1 max-w-[680px] border-[1.5px] border-[#e9ecef] rounded-[6px] bg-[#fafafa] transition-all duration-[220ms] focus-within:border-[#FFD500] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,213,0,0.12)] relative">
            <!-- Category Dropdown -->
            <div class="relative shrink-0">
              <button
                class="bg-white border-none border-r-[1.5px] border-r-[#e9ecef] px-[1.125rem] py-3 text-[0.875rem] font-semibold cursor-pointer flex items-center gap-[0.375rem] whitespace-nowrap hover:bg-[#f5f5f5] transition-colors duration-[220ms] rounded-l-[6px]"
                @click="toggleSearchCategory"
              >
                {{ selectedSearchCategory }}<i class="bi bi-chevron-down text-[0.6875rem] text-[#888]"></i>
              </button>
              <transition name="fade-slide">
                <div v-if="showSearchCategory" class="absolute top-[calc(100%+4px)] left-0 bg-white border-[1.5px] border-[#e9ecef] rounded-[6px] shadow-[0_6px_20px_rgba(0,0,0,0.08)] min-w-[190px] z-[200]">
                  <button
                    v-for="cat in searchCategories"
                    :key="cat.value"
                    @click="selectSearchCategory(cat)"
                    class="w-full bg-none border-none px-[1.125rem] py-3 text-left text-[0.875rem] cursor-pointer hover:bg-[#fffbe6] transition-colors duration-[220ms]"
                  >{{ cat.label }}</button>
                </div>
              </transition>
            </div>

            <!-- Input -->
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              @keyup.enter="handleSearch"
              @input="onSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keydown.down.prevent="moveSuggestion(1)"
              @keydown.up.prevent="moveSuggestion(-1)"
              @keydown.escape="closeSuggestions"
              type="text"
              autocomplete="off"
              class="flex-1 px-[0.875rem] py-3 border-none outline-none bg-transparent text-[0.875rem] placeholder:text-[#aaa] placeholder:font-mono"
              placeholder="Zoek naar..."
            >

            <!-- Zoeksuggesties dropdown -->
            <transition name="fade-slide">
              <div
                v-if="showSuggestions && (suggestions.length > 0 || suggestionsLoading)"
                class="suggestions-dropdown absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[#e9ecef] rounded-[10px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[300] overflow-hidden"
              >
                <!-- Loading -->
                <div v-if="suggestionsLoading && suggestions.length === 0" class="px-4 py-3 text-[0.8rem] text-[#888] flex items-center gap-2">
                  <i class="bi bi-arrow-repeat spin text-[#FFD500]"></i> Zoeken…
                </div>

                <!-- Resultaten -->
                <template v-else>
                  <router-link
                    v-for="(item, i) in suggestions"
                    :key="item.id"
                    :to="`/product/${item.slug}`"
                    @click="closeSuggestions"
                    class="suggestion-item flex items-center gap-3 px-4 py-2.5 no-underline transition-colors duration-150 border-b border-[#f5f5f5] last:border-b-0"
                    :class="{ 'bg-[#fffbe6]': i === activeSuggestion }"
                  >
                    <!-- Thumbnail -->
                    <div class="w-10 h-10 shrink-0 rounded-[5px] overflow-hidden bg-[#f8f9fa] border border-[#f0f0f0] flex items-center justify-center">
                      <img v-if="item.image" :src="item.image" :alt="item.name" class="w-full h-full object-contain">
                      <i v-else class="bi bi-image text-[#ccc] text-sm"></i>
                    </div>
                    <!-- Info -->
                    <div class="flex-1 min-w-0">
                      <div class="text-[0.85rem] font-medium text-[#111] truncate leading-snug">{{ item.name }}</div>
                      <div class="text-[0.75rem] text-[#888]">{{ item.category }}</div>
                    </div>
                    <!-- Prijs -->
                    <div class="shrink-0 text-right">
                      <div class="text-[0.875rem] font-bold text-[#111]">{{ formatPrice(item.price) }}</div>
                      <div v-if="item.on_sale && item.regular_price" class="text-[0.7rem] text-[#aaa] line-through">{{ formatPrice(item.regular_price) }}</div>
                    </div>
                  </router-link>

                  <!-- Alles bekijken -->
                  <button
                    @click="handleSearch"
                    class="w-full px-4 py-2.5 bg-[#fffbe6] text-[0.82rem] font-semibold text-[#333] text-left hover:bg-[#fff3b0] transition-colors flex items-center gap-2 border-none cursor-pointer"
                  >
                    <i class="bi bi-search text-[#FFD500]"></i>
                    Alle resultaten voor "<span class="text-[#111]">{{ searchQuery }}</span>"
                  </button>
                </template>
              </div>
            </transition>

            <!-- Aanbevolen zoektermen (lege, gefocuste zoekbalk) -->
            <transition name="fade-slide">
              <div
                v-if="showRecommended && !searchQuery.trim().length"
                class="suggestions-dropdown absolute top-[calc(100%+6px)] left-0 right-0 bg-white border border-[#e9ecef] rounded-[10px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[300] overflow-hidden"
              >
                <div class="px-4 pt-3 pb-1.5 text-[0.7rem] font-bold uppercase tracking-[0.6px] text-[#aaa]">
                  Aanbevolen<template v-if="selectedSearchSlug !== 'all'"> in {{ selectedSearchCategory }}</template>
                </div>
                <button
                  v-for="term in activeRecommended"
                  :key="term"
                  type="button"
                  @mousedown.prevent="pickRecommended(term)"
                  class="w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-[0.85rem] text-[#333] hover:bg-[#fffbe6] transition-colors border-none bg-transparent cursor-pointer border-b border-[#f5f5f5] last:border-b-0"
                >
                  <i class="bi bi-search text-[#FFD500] text-[0.8rem]"></i>
                  <span>{{ term }}</span>
                </button>
              </div>
            </transition>

            <!-- Search Button -->
            <button
              type="button"
              aria-label="Zoeken"
              @click="handleSearch"
              class="bg-[#FFD500] border-none px-7 text-black cursor-pointer text-base hover:bg-[#ffb800] transition-colors duration-[220ms] rounded-r-[6px]"
            >
              <i class="bi bi-search" aria-hidden="true"></i>
            </button>
          </div>

          <!-- Icons -->
          <div class="flex items-center gap-[0.25rem] ml-auto">
            <router-link to="/account" aria-label="Mijn account" class="relative hidden md:flex w-11 h-11 items-center justify-center text-[#555] no-underline rounded-[6px] text-lg hover:bg-gray-100 hover:text-[#111] transition-all duration-[220ms]">
              <i class="bi bi-person" aria-hidden="true"></i>
            </router-link>
            <router-link to="/wishlist" aria-label="Verlanglijst" class="relative flex w-10 h-10 md:w-11 md:h-11 items-center justify-center text-[#555] no-underline rounded-[6px] text-lg hover:bg-gray-100 hover:text-[#111] transition-all duration-[220ms]">
              <i class="bi bi-heart"></i>
              <span v-if="wishlistCount > 0" class="absolute top-px right-px bg-red-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[0.625rem] font-bold border-2 border-white">{{ wishlistCount }}</span>
            </router-link>
            <router-link to="/cart" aria-label="Winkelwagen" class="relative w-10 h-10 md:w-11 md:h-11 flex items-center justify-center bg-[#FFD500] text-black no-underline rounded-[6px] text-lg hover:bg-[#ffb800] transition-all duration-[220ms]">
              <i class="bi bi-cart" aria-hidden="true"></i>
              <span v-if="cartItemCount > 0" class="absolute top-px right-px bg-red-500 text-white rounded-full w-[18px] h-[18px] flex items-center justify-center text-[0.625rem] font-bold border-2 border-white">{{ cartItemCount }}</span>
            </router-link>
          </div>

        </div>
      </div>
    </div>

    <!-- ④ Navigation Bar -->
    <div class="nav-bar hidden md:block bg-white border-t border-[#e9ecef]" @mouseleave="closeAllMenus">
      <div class="max-w-screen-xl mx-auto px-4">
        <div class="flex items-center justify-between">
          <nav class="flex items-stretch gap-0 flex-1">

            <!-- Home -->
            <router-link to="/" class="nav-link px-4 h-12 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem]">
              <i class="bi bi-house"></i><span>Home</span>
            </router-link>

            <!-- Zonnepanelen -->
            <div class="flex items-stretch" @mouseenter="openMenu('zonnepanelen')">
              <div class="nav-link px-4 h-12 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem] cursor-pointer"
                :class="{ active: activeMenu === 'zonnepanelen' }"
                @click="toggleMenu('zonnepanelen')"
              >
                <span>Zonnepanelen</span>
                <i class="bi bi-chevron-down text-[0.625rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'zonnepanelen' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'zonnepanelen'" class="mega-menu">
                  <div class="mega-menu-inner">
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Shop per Type</h3>
                      <div class="flex flex-col gap-[0.375rem]">
                        <router-link v-for="cat in solarCategories" :key="cat.id" :to="`/categorie/${cat.slug}`" class="menu-item-card" @click="closeAllMenus">
                          <div class="w-9 h-9 shrink-0 bg-white rounded-[5px] flex items-center justify-center text-base text-[#FFD500] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><i :class="cat.icon"></i></div>
                          <div class="flex-1 min-w-0">
                            <strong class="block text-[0.8125rem] text-[#111] whitespace-nowrap overflow-hidden text-ellipsis">{{ cat.name }}</strong>
                            <small class="block text-[0.75rem] text-[#888]">{{ cat.description }}</small>
                          </div>
                          <i class="bi bi-chevron-right text-[0.6875rem] text-[#ccc] opacity-0 group-hover:opacity-100 shrink-0"></i>
                        </router-link>
                      </div>
                    </div>
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Populaire Merken</h3>
                      <div class="grid grid-cols-2 gap-2 mb-[0.875rem]">
                        <router-link v-for="brand in solarBrands.slice(0,6)" :key="brand.id" :to="`/merk/${brand.slug}`" class="brand-compact-card" @click="closeAllMenus">
                          <img :src="brand.logo" :alt="brand.name">
                        </router-link>
                      </div>
                      <router-link to="/merken" class="inline-flex items-center gap-[0.375rem] text-[#b8860b] no-underline text-[0.8125rem] font-semibold hover:gap-[0.625rem] hover:text-[#000] transition-all duration-200" @click="closeAllMenus">
                        Alle merken <i class="bi bi-arrow-right"></i>
                      </router-link>
                    </div>
                    <div class="flex flex-col">
                      <div class="menu-promo rounded-[10px] p-6 pt-10 flex flex-col h-full relative" style="background: linear-gradient(135deg, #FFD500, #FFC107)">
                        <span class="absolute top-3 left-4 inline-flex items-center gap-[0.375rem] bg-black/10 px-3 py-1 rounded-full text-[0.6875rem] font-bold uppercase tracking-[0.6px]">
                          <i class="bi bi-lightning-charge"></i> ACTIE
                        </span>
                        <h4 class="text-[1.125rem] font-black text-[#111] mb-[0.375rem] leading-tight">Complete Zonne-energie Sets</h4>
                        <p class="text-[#444] text-[0.8125rem] mb-[0.875rem]">Bespaar tot <strong>€500,-</strong> op complete installaties</p>
                        <ul class="list-none p-0 m-0 mb-4">
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> Panelen + Omvormer + Montage</li>
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> Plug & Play installatie</li>
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> Direct opbrengst</li>
                        </ul>
                        <router-link to="/actie/balkon-zonnepanelen" class="mt-auto self-start bg-[#111] text-white px-[1.125rem] py-[0.625rem] rounded-[6px] no-underline inline-flex items-center gap-2 font-bold text-[0.8125rem] hover:bg-[#333] hover:translate-x-0.5 transition-all duration-[220ms]" @click="closeAllMenus">
                          Bekijk Aanbiedingen <i class="bi bi-arrow-right"></i>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Laadpalen -->
            <div class="flex items-stretch" @mouseenter="openMenu('laadpalen')">
              <div class="nav-link px-4 h-12 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem] cursor-pointer"
                :class="{ active: activeMenu === 'laadpalen' }"
                @click="toggleMenu('laadpalen')"
              >
                <span>Laadpalen</span>
                <i class="bi bi-chevron-down text-[0.625rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'laadpalen' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'laadpalen'" class="mega-menu">
                  <div class="mega-menu-inner">
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Laadpalen</h3>
                      <div class="flex flex-col gap-[0.375rem]">
                        <router-link v-for="cat in chargingCategories" :key="cat.id" :to="`/categorie/${cat.slug}`" class="menu-item-card" @click="closeAllMenus">
                          <div class="w-9 h-9 shrink-0 bg-white rounded-[5px] flex items-center justify-center text-base text-[#FFD500] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><i :class="cat.icon"></i></div>
                          <div class="flex-1 min-w-0">
                            <strong class="block text-[0.8125rem] text-[#111] whitespace-nowrap overflow-hidden text-ellipsis">{{ cat.name }}</strong>
                            <small class="block text-[0.75rem] text-[#888]">{{ cat.description }}</small>
                          </div>
                          <i class="bi bi-chevron-right text-[0.6875rem] text-[#ccc] opacity-0 shrink-0"></i>
                        </router-link>
                      </div>
                      <!-- Laadkabels als compacte subsectie -->
                      <div class="mt-4 pt-4 border-t border-[#f0f0f0]">
                        <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.625rem]">Laadkabels</h3>
                        <div class="grid grid-cols-2 gap-[0.375rem]">
                          <router-link v-for="cable in cableCategories" :key="cable.id" :to="`/categorie/${cable.slug}`" class="menu-item-compact" @click="closeAllMenus">
                            <div class="w-7 h-7 shrink-0 bg-white rounded-[4px] flex items-center justify-center text-[0.8125rem] text-[#FFD500] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><i :class="cable.icon"></i></div>
                            <div><strong class="block text-[0.75rem] text-[#111] whitespace-nowrap overflow-hidden text-ellipsis">{{ cable.name }}</strong><small class="block text-[0.65rem] text-[#888]">{{ cable.description }}</small></div>
                          </router-link>
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Top Merken</h3>
                      <div class="grid grid-cols-2 gap-2 mb-[0.875rem]">
                        <router-link v-for="brand in chargingBrands.slice(0,6)" :key="brand.id" :to="`/merk/${brand.slug}`" class="brand-compact-card" @click="closeAllMenus">
                          <img :src="brand.logo" :alt="brand.name">
                        </router-link>
                      </div>
                      <router-link to="/merken/laadpalen" class="inline-flex items-center gap-[0.375rem] text-[#b8860b] no-underline text-[0.8125rem] font-semibold hover:gap-[0.625rem] hover:text-[#000] transition-all duration-200" @click="closeAllMenus">
                        Alle merken <i class="bi bi-arrow-right"></i>
                      </router-link>
                    </div>
                    <div class="flex flex-col">
                      <div class="menu-promo rounded-[10px] p-6 pt-10 flex flex-col h-full relative" style="background: linear-gradient(135deg, #FFD500, #FFC107)">
                        <span class="absolute top-3 left-4 inline-flex items-center gap-[0.375rem] bg-black/10 px-3 py-1 rounded-full text-[0.6875rem] font-bold uppercase tracking-[0.6px]">
                          <i class="bi bi-lightning-charge"></i> POPULAIR
                        </span>
                        <h4 class="text-[1.125rem] font-black text-[#111] mb-[0.375rem] leading-tight">Slimme Laadpalen</h4>
                        <p class="text-[#444] text-[0.8125rem] mb-[0.875rem]">Laad slim en bespaar op je energiekosten</p>
                        <ul class="list-none p-0 m-0 mb-4">
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> Dynamisch laden</li>
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> App bediening</li>
                          <li class="flex items-center gap-2 text-[#333] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[0.7rem]"></i> Zonne-integratie</li>
                        </ul>
                        <router-link to="/categorie/slimme-laadpalen" class="mt-auto self-start bg-[#111] text-white px-[1.125rem] py-[0.625rem] rounded-[6px] no-underline inline-flex items-center gap-2 font-bold text-[0.8125rem] hover:bg-[#333] hover:translate-x-0.5 transition-all duration-[220ms]" @click="closeAllMenus">
                          Ontdek Slimme Laadpalen <i class="bi bi-arrow-right"></i>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Batterijen -->
            <div class="flex items-stretch" @mouseenter="openMenu('batterijen')">
              <div class="nav-link px-4 h-12 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem] cursor-pointer"
                :class="{ active: activeMenu === 'batterijen' }"
                @click="toggleMenu('batterijen')"
              >
                <span>Batterijen</span>
                <i class="bi bi-chevron-down text-[0.625rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'batterijen' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'batterijen'" class="mega-menu">
                  <div class="mega-menu-inner">
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Shop per Type</h3>
                      <div class="flex flex-col gap-[0.375rem]">
                        <router-link v-for="cat in batteryCategories" :key="cat.id" :to="`/categorie/${cat.slug}`" class="menu-item-card" @click="closeAllMenus">
                          <div class="w-9 h-9 shrink-0 bg-white rounded-[5px] flex items-center justify-center text-base text-[#FFD500] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><i :class="cat.icon"></i></div>
                          <div class="flex-1 min-w-0">
                            <strong class="block text-[0.8125rem] text-[#111] whitespace-nowrap overflow-hidden text-ellipsis">{{ cat.name }}</strong>
                            <small class="block text-[0.75rem] text-[#888]">{{ cat.description }}</small>
                          </div>
                          <i class="bi bi-chevron-right text-[0.6875rem] text-[#ccc] opacity-0 shrink-0"></i>
                        </router-link>
                      </div>
                    </div>
                    <div class="flex flex-col">
                      <h3 class="text-[0.6875rem] font-bold uppercase tracking-[0.8px] text-[#888] mb-[0.875rem]">Waarom opslaan?</h3>
                      <div class="flex flex-col gap-2 mb-4">
                        <div class="flex items-start gap-3">
                          <div class="w-8 h-8 shrink-0 bg-[#fffbe6] rounded-[5px] flex items-center justify-center text-[#FFD500]"><i class="bi bi-sun"></i></div>
                          <div><strong class="block text-[0.8rem] text-[#111]">Zonne-energie opslaan</strong><small class="text-[0.75rem] text-[#888]">Gebruik overdag opgewekte stroom ook 's avonds</small></div>
                        </div>
                        <div class="flex items-start gap-3">
                          <div class="w-8 h-8 shrink-0 bg-[#fffbe6] rounded-[5px] flex items-center justify-center text-[#FFD500]"><i class="bi bi-graph-down-arrow"></i></div>
                          <div><strong class="block text-[0.8rem] text-[#111]">Lagere energiekosten</strong><small class="text-[0.75rem] text-[#888]">Koop stroom op het goedkoopste moment</small></div>
                        </div>
                        <div class="flex items-start gap-3">
                          <div class="w-8 h-8 shrink-0 bg-[#fffbe6] rounded-[5px] flex items-center justify-center text-[#FFD500]"><i class="bi bi-shield-check"></i></div>
                          <div><strong class="block text-[0.8rem] text-[#111]">Onafhankelijkheid</strong><small class="text-[0.75rem] text-[#888]">Minder afhankelijk van het net</small></div>
                        </div>
                      </div>
                      <router-link to="/categorie/thuisbatterijen" class="inline-flex items-center gap-[0.375rem] text-[#b8860b] no-underline text-[0.8125rem] font-semibold hover:gap-[0.625rem] hover:text-[#000] transition-all duration-200" @click="closeAllMenus">
                        Alle batterijen <i class="bi bi-arrow-right"></i>
                      </router-link>
                    </div>
                    <div class="flex flex-col">
                      <div class="menu-promo rounded-[10px] p-6 pt-10 flex flex-col h-full relative" style="background: linear-gradient(135deg, #1a1a2e, #16213e)">
                        <span class="absolute top-3 left-4 inline-flex items-center gap-[0.375rem] bg-white/10 text-white px-3 py-1 rounded-full text-[0.6875rem] font-bold uppercase tracking-[0.6px]">
                          <i class="bi bi-battery-full"></i> NIEUW
                        </span>
                        <h4 class="text-[1.125rem] font-black text-white mb-[0.375rem] leading-tight">Thuisbatterij + Zonnepanelen</h4>
                        <p class="text-[#aaa] text-[0.8125rem] mb-[0.875rem]">Complete energieonafhankelijkheid voor je woning</p>
                        <ul class="list-none p-0 m-0 mb-4">
                          <li class="flex items-center gap-2 text-[#ccc] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> Tot 90% zelfvoorzienend</li>
                          <li class="flex items-center gap-2 text-[#ccc] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> Terugverdientijd 5–8 jaar</li>
                          <li class="flex items-center gap-2 text-[#ccc] text-[0.8rem] mb-[0.3rem]"><i class="bi bi-check2 text-[#FFD500] text-[0.7rem]"></i> 10 jaar garantie</li>
                        </ul>
                        <router-link to="/actie/thuisbatterij" class="mt-auto self-start bg-[#FFD500] text-[#111] px-[1.125rem] py-[0.625rem] rounded-[6px] no-underline inline-flex items-center gap-2 font-bold text-[0.8125rem] hover:bg-[#ffb800] hover:translate-x-0.5 transition-all duration-[220ms]" @click="closeAllMenus">
                          Bekijk combinaties <i class="bi bi-arrow-right"></i>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Diensten -->
            <div class="flex items-stretch" @mouseenter="openMenu('diensten')">
              <div class="nav-link px-4 h-12 text-[#444] font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem] cursor-pointer"
                :class="{ active: activeMenu === 'diensten' }"
                @click="toggleMenu('diensten')"
              >
                <span>Diensten</span>
                <i class="bi bi-chevron-down text-[0.625rem] text-[#888] transition-transform duration-200" :class="{ 'rotate-180': activeMenu === 'diensten' }"></i>
              </div>
              <transition name="menu-drop">
                <div v-if="activeMenu === 'diensten'" class="mega-menu">
                  <div class="mega-menu-inner compact">
                    <div class="col-span-full grid grid-cols-2 gap-2">
                      <router-link v-for="service in services" :key="service.id" :to="service.link" class="menu-item-compact" @click="closeAllMenus">
                        <div class="w-8 h-8 shrink-0 bg-white rounded-[5px] flex items-center justify-center text-[0.9375rem] text-[#FFD500] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"><i :class="service.icon"></i></div>
                        <div><strong class="block text-[0.8125rem] text-[#111]">{{ service.name }}</strong><small class="block text-[0.7rem] text-[#888]">{{ service.description }}</small></div>
                      </router-link>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Keuzehulp -->
            <router-link to="/keuzehulp" class="nav-link px-4 h-12 text-[#444] no-underline font-medium text-[0.875rem] whitespace-nowrap inline-flex items-center gap-[0.375rem]">
              <i class="bi bi-compass"></i><span>Keuzehulp</span>
            </router-link>

            <!-- Acties -->
            <router-link
              to="/acties"
              class="nav-link--acties inline-flex items-center gap-[0.375rem] px-[0.875rem] h-[30px] my-auto mx-2 rounded-full no-underline font-bold text-[0.8125rem] bg-[#FFD500] text-[#111] hover:bg-[#ffb800] transition-colors duration-[180ms]"
            >
              <i class="bi bi-lightning-charge text-[0.8rem]"></i><span>Acties</span>
            </router-link>

          </nav>

          <!-- Live chat -->
          <a
            href="/klantenservice#livechat"
            class="inline-flex items-center gap-[0.375rem] px-[0.875rem] h-[30px] my-auto ml-1 rounded-full no-underline font-bold text-[0.8125rem] text-[#111] bg-[#FFD500] hover:bg-[#ffb800] whitespace-nowrap transition-colors duration-[180ms] shrink-0"
          >
            <i class="bi bi-chat-dots text-[0.875rem]"></i><span>Live chat</span>
          </a>
        </div>
      </div>
    </div>

    <!-- ⑤ Mobile Menu -->
    <transition name="slide-right">
      <div v-if="showMobileMenu" class="fixed inset-0 bg-white z-[1001] flex flex-col">

        <!-- Menu header: logo + sluitknop -->
        <div class="flex items-center justify-between px-5 shrink-0 bg-white border-b border-[#f0f0f0]" style="height:64px">
          <router-link to="/" @click="closeMobileMenu" aria-label="Plug2Charge.nl - home" class="no-underline flex items-center">
            <Logo :height="38" />
          </router-link>
          <button
            @click="closeMobileMenu"
            class="w-10 h-10 flex items-center justify-center rounded-xl border border-[#e9eaec] text-[#555] bg-white hover:bg-[#f4f4f5] hover:border-[#ddd] transition-all"
            aria-label="Menu sluiten"
          >
            <i class="bi bi-x-lg text-[1rem]"></i>
          </button>
        </div>

        <!-- Scrollbare inhoud -->
        <div class="overflow-y-auto flex-1 p-5 flex flex-col gap-5">

          <!-- Zoekbalk -->
          <div class="flex gap-2">
            <input v-model="searchQuery" @keyup.enter="handleSearch" type="text" placeholder="Zoek producten..."
              class="flex-1 px-3 py-3 border-[1.5px] border-[#e9ecef] rounded-[8px] outline-none text-[0.9rem] focus:border-[#FFD500] focus:ring-2 focus:ring-[#FFD500]/20 transition-all">
            <button type="button" aria-label="Zoeken" @click="handleSearch" class="bg-[#FFD500] hover:bg-[#ffb800] border-none px-5 rounded-[8px] text-black cursor-pointer text-base transition-colors">
              <i class="bi bi-search" aria-hidden="true"></i>
            </button>
          </div>

          <!-- Categorieën -->
          <div>
            <p class="text-[0.7rem] font-bold uppercase tracking-wider text-[#aaa] mb-2 px-1">Categorieën</p>
            <nav class="flex flex-col gap-1">
              <router-link to="/categorie/zonnepanelen" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#fffbe6] flex items-center justify-center shrink-0"><i class="bi bi-sun text-[#FFD500]"></i></span>
                <span class="font-medium text-[#111]">Zonnepanelen</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/categorie/laadpalen" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#fffbe6] flex items-center justify-center shrink-0"><i class="bi bi-plug text-[#FFD500]"></i></span>
                <span class="font-medium text-[#111]">Laadpalen & Kabels</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/categorie/thuisbatterijen" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#fffbe6] flex items-center justify-center shrink-0"><i class="bi bi-battery-full text-[#FFD500]"></i></span>
                <span class="font-medium text-[#111]">Batterijen</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/acties" class="mobile-nav-item !border-[#FFD500]/30 !bg-[#fffbe6]" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#FFD500] flex items-center justify-center shrink-0"><i class="bi bi-tag text-[#111]"></i></span>
                <span class="font-semibold text-[#b8860b]">Acties</span>
                <i class="bi bi-chevron-right text-[#FFD500] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/keuzehulp" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#fffbe6] flex items-center justify-center shrink-0"><i class="bi bi-compass text-[#FFD500]"></i></span>
                <span class="font-medium text-[#111]">Keuzehulp</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/gratis-energieadvies" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#fffbe6] flex items-center justify-center shrink-0"><i class="bi bi-chat-heart text-[#FFD500]"></i></span>
                <span class="font-medium text-[#111]">Gratis energieadvies</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
            </nav>
          </div>

          <!-- Account -->
          <div>
            <p class="text-[0.7rem] font-bold uppercase tracking-wider text-[#aaa] mb-2 px-1">Account</p>
            <nav class="flex flex-col gap-1">
              <router-link to="/account" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#f3f4f6] flex items-center justify-center shrink-0"><i class="bi bi-person text-[#555]"></i></span>
                <span class="font-medium text-[#111]">Mijn Account</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/wishlist" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#f3f4f6] flex items-center justify-center shrink-0"><i class="bi bi-heart text-[#555]"></i></span>
                <span class="font-medium text-[#111]">Verlanglijst</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
              <router-link to="/diensten" class="mobile-nav-item" @click="closeMobileMenu">
                <span class="w-8 h-8 rounded-lg bg-[#f3f4f6] flex items-center justify-center shrink-0"><i class="bi bi-briefcase text-[#555]"></i></span>
                <span class="font-medium text-[#111]">Diensten</span>
                <i class="bi bi-chevron-right text-[#ccc] text-[0.7rem] ml-auto"></i>
              </router-link>
            </nav>
          </div>

          <!-- Contact -->
          <div class="bg-[#f8f9fa] rounded-xl p-4 flex items-center gap-3">
            <span class="w-9 h-9 rounded-lg bg-[#FFD500] flex items-center justify-center shrink-0"><i class="bi bi-chat-dots text-[#111] text-base"></i></span>
            <div>
              <p class="text-[0.8rem] font-bold text-[#111] mb-0">Hulp nodig?</p>
              <a href="/klantenservice#livechat" class="text-[0.78rem] text-[#111] font-semibold no-underline">Live chat</a>
            </div>
          </div>

        </div>
      </div>
    </transition>

    <!-- Overlay -->
    <transition name="fade">
      <div v-if="showSearchCategory || showMobileMenu" class="fixed inset-0 bg-black/35 z-[99]" @click="closeAll"></div>
    </transition>

  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import Logo from '@components/Logo.vue'
import { useAnalytics } from '@composables/useAnalytics'

const router    = useRouter()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const { trackSearch } = useAnalytics()

const searchQuery        = ref('')
const selectedSearchCategory = ref('Alles')
const selectedSearchSlug = ref('all')   // slug van de gekozen zoekcategorie (voor de filter-query)
const showSearchCategory = ref(false)
const showRecommended    = ref(false)   // paneel met aanbevolen zoektermen (lege, gefocuste zoekbalk)
const activeMenu         = ref(null)
const showMobileMenu     = ref(false)

// ── Zoeksuggesties ────────────────────────────────────────────────────────────
const suggestions       = ref([])
const suggestionsLoading = ref(false)
const showSuggestions   = ref(false)
const activeSuggestion  = ref(-1)
let   suggestDebounce   = null

import { formatPrice } from '@/utils/price'

const fetchSuggestions = async (q) => {
  if (q.length < 2) { suggestions.value = []; suggestionsLoading.value = false; return }
  suggestionsLoading.value = true
  try {
    const res = await fetch(`/_search?q=${encodeURIComponent(q)}`)
    if (res.ok) suggestions.value = await res.json()
  } catch (_) {
    suggestions.value = []
  } finally {
    suggestionsLoading.value = false
  }
}

const onSearchInput = () => {
  activeSuggestion.value = -1
  const hasQuery = searchQuery.value.trim().length >= 2
  showSuggestions.value = hasQuery
  showRecommended.value = !searchQuery.value.trim().length   // leeg → aanbevolen termen tonen
  clearTimeout(suggestDebounce)
  if (hasQuery) {
    suggestionsLoading.value = true
    suggestDebounce = setTimeout(() => fetchSuggestions(searchQuery.value.trim()), 220)
  } else {
    suggestions.value = []
    suggestionsLoading.value = false
  }
}

const onSearchFocus = () => {
  pauseTyping()
  if (searchQuery.value.trim().length >= 2) showSuggestions.value = true
  else showRecommended.value = true   // lege zoekbalk → toon aanbevolen zoektermen
}

const onSearchBlur = () => {
  // Kleine vertraging zodat een klik op een suggestie/term nog registreert
  setTimeout(() => {
    showSuggestions.value = false
    showRecommended.value = false
    activeSuggestion.value = -1
  }, 200)
  resumeTyping()
}

const closeSuggestions = () => {
  showSuggestions.value = false
  activeSuggestion.value = -1
  suggestions.value = []
}

const moveSuggestion = (dir) => {
  if (!showSuggestions.value || suggestions.value.length === 0) return
  const max = suggestions.value.length // +1 voor de "alle resultaten" knop
  activeSuggestion.value = Math.max(-1, Math.min(max - 1, activeSuggestion.value + dir))
}
const wishlistCount = computed(() => wishlistStore.itemCount)
const countdownTime    = ref('')     // leeg op SSR - wordt in onMounted ingesteld (hydration-safe)
const cutoffPassed     = ref(false)
const isScrolled = ref(false)

// Roterende topbar-berichten (0 = urgentie, 1 = aanbieding, 2 = vertrouwen)
const activeTopbarMsg = ref(0)
let topbarInterval = null

const searchInputRef = ref(null)
let typingIndex = 0
let charIndex = 0
let isDeleting = false
let isTypingPaused = false
let typingTimeout = null

const lockScroll = () => {
  if (typeof window === 'undefined') return
  const y = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.top = `-${y}px`
  document.body.style.width = '100%'
  document.body.style.overflowY = 'scroll'
}

const unlockScroll = () => {
  if (typeof window === 'undefined') return
  const y = document.body.style.top
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  document.body.style.overflowY = ''
  window.scrollTo(0, parseInt(y || '0') * -1)
}

watch(showMobileMenu, (mobile) => { mobile ? lockScroll() : unlockScroll() })

const handleScroll = () => {
  if (typeof window === 'undefined') return
  isScrolled.value = window.scrollY > 60
}

const searchCategories = [
  { value: 'all', label: 'Alles' },
  { value: 'zonnepanelen', label: 'Zonnepanelen' },
  { value: 'laadpalen', label: 'Laadpalen' },
  { value: 'laadkabels', label: 'Laadkabels' },
  { value: 'accessoires', label: 'Accessoires' }
]

// Aanbevolen zoektermen per categorie-slug. Passen mee met de gekozen categorie
// in de zoekbalk; klikken vult de term in en zoekt binnen die categorie.
const recommendedTerms = {
  all:          ['zonnepanelen', 'thuisbatterij', 'laadpaal', 'balkon zonnepanelen', 'micro-omvormer'],
  zonnepanelen: ['balkon zonnepanelen', 'complete set', 'micro-omvormer', 'montageset', '420W paneel'],
  laadpalen:    ['thuislaadpaal', '11 kW laadpaal', 'laadpaal met app', 'type 2 laadpaal', 'laadpaal zakelijk'],
  laadkabels:   ['type 2 kabel', 'laadkabel 5 meter', 'snellaadkabel', '3-fase laadkabel', 'spiraalkabel'],
  accessoires:  ['kabelhaak', 'laadpas', 'montagemateriaal', 'omvormer accessoires', 'meterkast'],
}
const activeRecommended = computed(() => recommendedTerms[selectedSearchSlug.value] || recommendedTerms.all)

const searchTerms = [
  'zonnepanelen', 'balkon zonnepanelen', 'laadpalen', 'slimme laadpalen',
  'laadkabels Type 2', 'micro omvormers', 'batterijopslag', 'thuislaadpaal',
  'snellaad kabels', 'complete sets', 'installatie service', 'zonnepaneel accessoires'
]

const solarCategories = [
  { id: 1, name: 'Balkon Zonnepanelen', slug: 'balkon-zonnepanelen', icon: 'bi bi-sun', description: 'Plug & play voor balkon' },
  { id: 2, name: 'Complete Sets', slug: 'zonnepanelen-sets', icon: 'bi bi-box-seam', description: 'Alles-in-één pakketten' },
  { id: 3, name: 'Losse Panelen', slug: 'losse-zonnepanelen', icon: 'bi bi-sun', description: 'Individuele panelen' },
  { id: 4, name: 'Micro Omvormers', slug: 'micro-omvormers', icon: 'bi bi-gear', description: 'Optimale conversie' },
  { id: 5, name: 'Montage Materiaal', slug: 'montage-materiaal', icon: 'bi bi-wrench', description: 'Beugels & rails' },
]

const batteryCategories = [
  { id: 1, name: 'Thuisbatterijen', slug: 'thuisbatterijen', icon: 'bi bi-battery-full', description: 'Koppel aan je zonnepanelen' },
  { id: 2, name: 'Draagbare Batterijen', slug: 'draagbare-batterijen', icon: 'bi bi-battery', description: 'Energie overal mee naartoe' },
  { id: 3, name: 'All-in-One Systemen', slug: 'all-in-one-batterijen', icon: 'bi bi-box-seam', description: 'Panelen + opslag in één' },
  { id: 4, name: 'Batterij Accessoires', slug: 'batterij-accessoires', icon: 'bi bi-tools', description: 'Kabels, BMS & meer' },
]

const chargingCategories = [
  { id: 1, name: 'Slimme Laadpalen', slug: 'slimme-laadpalen', icon: 'bi bi-lightbulb', description: 'Met app & dynamisch laden' },
  { id: 2, name: 'Thuislaadpalen', slug: 'thuislaadpalen', icon: 'bi bi-house', description: 'Voor thuis installatie' },
  { id: 3, name: 'Zakelijke Laadpalen', slug: 'zakelijke-laadpalen', icon: 'bi bi-building', description: 'Voor bedrijven' },
  { id: 4, name: 'Mobiele Laders', slug: 'mobiele-laders', icon: 'bi bi-battery', description: 'Draagbare oplossingen' },
  { id: 5, name: '3-Fase Laadpalen', slug: '3-fase-laadpalen', icon: 'bi bi-lightning-charge', description: 'Snelladen tot 22kW' },
  { id: 6, name: 'Met Zonne-energie', slug: 'laadpalen-zonne-energie', icon: 'bi bi-sun', description: 'Gecombineerd systeem' }
]

const cableCategories = [
  { id: 1, name: 'Type 1 Kabels', slug: 'type-1-kabels', icon: 'bi bi-plug', description: "Voor Aziatische auto's" },
  { id: 2, name: 'Type 2 Kabels', slug: 'type-2-kabels', icon: 'bi bi-plug', description: 'Europese standaard' },
  { id: 3, name: 'Spiraal Kabels', slug: 'spiraal-kabels', icon: 'bi bi-plug', description: 'Compact & flexibel' },
  { id: 4, name: 'Snellaad Kabels', slug: 'snellaad-kabels', icon: 'bi bi-lightning-charge', description: 'Tot 22kW' },
  { id: 5, name: 'Verlengkabels', slug: 'verlengkabels', icon: 'bi bi-arrows-expand', description: 'Extra bereik' },
  { id: 6, name: 'Kabel Tassen', slug: 'kabel-tassen', icon: 'bi bi-bag', description: 'Voor opslag' }
]

const services = [
  { id: 1, name: 'Installatie Service', icon: 'bi bi-wrench', description: 'Professionele montage', link: '/diensten/installatie' },
  { id: 2, name: 'Gratis Advies', icon: 'bi bi-chat-text', description: 'Persoonlijk adviesgesprek', link: '/advies' },
  { id: 3, name: 'Garantie & Service', icon: 'bi bi-shield-check', description: 'Tot 25 jaar garantie', link: '/garantie' },
  { id: 4, name: 'Subsidies & Regelingen', icon: 'bi bi-coin', description: 'Maximale subsidie', link: '/subsidies' }
]

// Omvormers & zonnepanelen merken
const solarBrands = [
  { id: 193, name: 'Aiko',         slug: 'aiko',         logo: '/wp-content/uploads/2026/05/aiko-logo-1024x302-1.webp' },
  { id: 197, name: 'Enphase',      slug: 'enphase',      logo: '/wp-content/uploads/2026/05/Logo-768x427-1.png' },
  { id: 188, name: 'GoodWe',       slug: 'goodwe',       logo: '/wp-content/uploads/2026/05/6272bc428d0a81741d601d72_goodwe.png' },
  { id: 187, name: 'Growatt',      slug: 'growatt',      logo: '/wp-content/uploads/2026/05/Growatt_logo-new-GB.png' },
  { id: 21,  name: 'Hoymiles',     slug: 'hoymiles',     logo: '/wp-content/uploads/2026/04/Hoymiles_logo.png' },
  { id: 200, name: 'SMA',          slug: 'sma',          logo: '/wp-content/uploads/2026/05/logo-sma-small.png' },
]

// Laadpalen, opslag & energie-merken
const chargingBrands = [
  { id: 190, name: 'Hager',         slug: 'hager',         logo: '/wp-content/uploads/2026/05/hager-logo.383x126.png' },
  { id: 167, name: 'Victron Energy', slug: 'victron-energy', logo: '/wp-content/uploads/2026/04/3840px-Victron_Energy_Logo.svg_-scaled.png' },
  { id: 172, name: 'EcoFlow',        slug: 'ecoflow',        logo: '/wp-content/uploads/2026/04/EcoFlow-Logo2-scaled.png' },
  { id: 171, name: 'Marstek',        slug: 'marstek',        logo: '/wp-content/uploads/2026/04/marstek-quote-long-1.webp' },
  { id: 191, name: 'Anker',          slug: 'anker',          logo: '/wp-content/uploads/2026/05/Anker_logo.svg_.png' },
  { id: 158, name: 'Jackery',        slug: 'jackery',        logo: '/wp-content/uploads/2026/04/Jackery_logo_2022-10.png' },
]

const cartItemCount = computed(() => cartStore.itemCount)

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    const term = searchQuery.value.trim()
    trackSearch(term)
    const query = { search: term }
    if (selectedSearchSlug.value !== 'all') query.category = selectedSearchSlug.value
    router.push({ path: '/producten', query })
    searchQuery.value = ''
    closeSuggestions()
    closeAll()
  }
}

const toggleSearchCategory = () => { showSearchCategory.value = !showSearchCategory.value }
const selectSearchCategory = (cat) => {
  selectedSearchCategory.value = cat.label
  selectedSearchSlug.value = cat.value
  showSearchCategory.value = false
}

// Klik op een aanbevolen term: invullen en direct zoeken (binnen de categorie).
const pickRecommended = (term) => {
  searchQuery.value = term
  showRecommended.value = false
  handleSearch()
}
const openMenu = (name) => { activeMenu.value = name }
const toggleMenu = (name) => { activeMenu.value = activeMenu.value === name ? null : name }
const closeAllMenus = () => { activeMenu.value = null }
const toggleMobileMenu = () => { showMobileMenu.value = !showMobileMenu.value }
const closeMobileMenu = () => { showMobileMenu.value = false }
const closeAll = () => { showSearchCategory.value = false; showMobileMenu.value = false; closeAllMenus() }

const setPlaceholder = (text) => { if (searchInputRef.value) searchInputRef.value.placeholder = text }

const typeText = () => {
  if (isTypingPaused) return
  const term = searchTerms[typingIndex]
  const base = 'Zoek naar '
  if (!isDeleting) {
    charIndex++
    setPlaceholder(base + term.substring(0, charIndex))
    if (charIndex >= term.length) {
      typingTimeout = setTimeout(() => { isDeleting = true; typeText() }, 2500)
    } else {
      typingTimeout = setTimeout(typeText, 120)
    }
  } else {
    charIndex--
    setPlaceholder(base + term.substring(0, charIndex))
    if (charIndex <= 0) {
      isDeleting = false
      typingIndex = (typingIndex + 1) % searchTerms.length
      typingTimeout = setTimeout(typeText, 600)
    } else {
      typingTimeout = setTimeout(typeText, 60)
    }
  }
}

const pauseTyping = () => { isTypingPaused = true; setPlaceholder('Waar ben je naar op zoek?') }
const resumeTyping = () => { if (!searchQuery.value.trim()) { isTypingPaused = false; typeText() } }

const CUTOFF_HOUR = 15 // bestellen vóór 15:00 = morgen in huis

const updateCountdown = () => {
  const now = new Date()
  const end = new Date()
  end.setHours(CUTOFF_HOUR, 0, 0, 0)

  // Bepaal of de cut-off vandaag al voorbij is
  cutoffPassed.value = now >= end

  // Tel altijd naar de eerstvolgende 15:00
  if (cutoffPassed.value) end.setDate(end.getDate() + 1)

  const d = end - now
  const h = Math.floor(d / 3600000)
  const m = Math.floor((d % 3600000) / 60000)
  const s = Math.floor((d % 60000) / 1000)
  countdownTime.value = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
}

let countdownInterval
onMounted(() => {
  wishlistStore.initWishlist()
  updateCountdown()
  countdownInterval = setInterval(updateCountdown, 1000)
  window.addEventListener('scroll', handleScroll, { passive: true })
  typeText()

  // Roteer topbar-berichten elke 4 seconden
  topbarInterval = setInterval(() => {
    activeTopbarMsg.value = (activeTopbarMsg.value + 1) % 3
  }, 4000)
})

onUnmounted(() => {
  if (countdownInterval) clearInterval(countdownInterval)
  if (topbarInterval)    clearInterval(topbarInterval)
  if (typingTimeout)     clearTimeout(typingTimeout)
  if (typeof window !== 'undefined') {
    window.removeEventListener('scroll', handleScroll)
    unlockScroll()
  }
})
</script>

<style scoped>
/* ── Topbar roterende berichten ──────────────────────── */
.topbar-messages { min-width: 0; }

/* slide-up in, slide-up out */
.topbar-slide-enter-active { transition: opacity 0.35s ease, transform 0.35s ease; }
.topbar-slide-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; position: absolute; }
.topbar-slide-enter-from   { opacity: 0; transform: translateY(8px); }
.topbar-slide-leave-to     { opacity: 0; transform: translateY(-8px); }

/* ── Nav link states ─────────────────────────────────── */
.nav-link {
  border-radius: 4px 4px 0 0;
  box-shadow: inset 0 -2px 0 0 transparent;
  transition: color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}

/* Hover: subtiel grijs achtergrond, GEEN underline - botst anders met actieve indicator */
.nav-link:hover {
  color: #111;
  background: #f4f4f5;
}

/* Dropdown open (.active): gele underline + lichtgele tint */
.nav-link.active {
  color: #111;
  font-weight: 600;
  background: #fffbeb;
  box-shadow: inset 0 -3px 0 0 #FFD500;
}

/* Huidige pagina via router: gele underline, transparante achtergrond */
.nav-link.router-link-active,
.nav-link.router-link-exact-active {
  color: #111;
  font-weight: 600;
  background: transparent;
  box-shadow: inset 0 -3px 0 0 #FFD500;
}

/* Acties & WhatsApp: pill-stijl, geen nav-link underline/bg */
.nav-link--acties { box-shadow: none !important; background: none !important; }

/* ── Nav-bar als positioning context voor mega menu's ── */
.nav-bar { position: relative; }

/* ── Mega menu ──────────────────────────────────────── */
.mega-menu {
  position: absolute;
  top: 100%; left: 50%; right: auto;
  transform: translateX(-50%);
  width: min(1180px, 96vw);
  background: #fff;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 12px 32px rgba(0,0,0,0.10);
  padding: 1.75rem 1.5rem;
  z-index: 100;
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  will-change: opacity;
}

.mega-menu-inner {
  display: grid;
  grid-template-columns: 1fr 220px 300px;
  gap: 2rem;
}

.mega-menu-inner.compact {
  grid-template-columns: 1fr 260px;
  max-width: 820px;
  margin: 0 auto;
}

/* ── Menu cards ─────────────────────────────────────── */
.menu-item-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: #fafafa;
  border: 1.5px solid transparent;
  border-radius: 6px;
  text-decoration: none;
  color: #333;
  transition: border-color 0.22s ease, background 0.22s ease, transform 0.22s ease;
}
.menu-item-card:hover {
  border-color: #FFD500;
  background: #fff;
  transform: translateX(3px);
}

.menu-item-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  background: #fafafa;
  border: 1.5px solid transparent;
  border-radius: 6px;
  text-decoration: none;
  color: #333;
  transition: border-color 0.22s ease, background 0.22s ease;
}
.menu-item-compact:hover {
  border-color: #FFD500;
  background: #fff;
}

.brand-compact-card {
  background: #fafafa;
  border: 1.5px solid #e9ecef;
  border-radius: 6px;
  padding: 0.5rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 2/1;
  transition: border-color 0.22s ease, background 0.22s ease, transform 0.22s ease;
}
.brand-compact-card:hover {
  border-color: #FFD500;
  background: #fff;
  transform: translateY(-2px);
}
.brand-compact-card img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: grayscale(100%);
  opacity: 0.5;
  transition: filter 0.22s ease, opacity 0.22s ease;
}
.brand-compact-card:hover img {
  filter: grayscale(0%);
  opacity: 1;
}

/* ── Mobile nav item ────────────────────────────────── */
.mobile-nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: #fafafa;
  border: 1.5px solid transparent;
  border-radius: 10px;
  text-decoration: none;
  color: #333;
  font-size: 0.9375rem;
  transition: background 0.18s ease, border-color 0.18s ease;
}
.mobile-nav-item:hover,
.mobile-nav-item.router-link-active { background: #fffbe6; border-color: #FFD500; }

/* ── Vue transitions ────────────────────────────────── */
.menu-drop-enter-active { transition: opacity 0.15s ease; }
.menu-drop-leave-active { transition: opacity 0.10s ease; }
.menu-drop-enter-from, .menu-drop-leave-to { opacity: 0; }

.fade-slide-enter-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.fade-slide-leave-active { transition: opacity 0.12s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(-4px); }
.fade-slide-leave-to { opacity: 0; }

/* ── Zoeksuggesties ── */
.suggestions-dropdown { max-height: 420px; overflow-y: auto; }
.suggestion-item { color: #111; }
.suggestion-item:hover { background: #fffbe6; }

.slide-right-enter-active { transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease; }
.slide-right-leave-active { transition: transform 0.22s ease, opacity 0.22s ease; }
.slide-right-enter-from, .slide-right-leave-to { transform: translateX(100%); opacity: 0; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ── Accessibility ──────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
button:focus-visible, a:focus-visible, input:focus-visible {
  outline: 2px solid #FFD500;
  outline-offset: 2px;
}
</style>